import { FootprintPolygonsType, LevelType, PositionType } from 'data/types';
import polygonClipping, { MultiPolygon, Polygon } from 'polygon-clipping';
import { BufferAttribute, Path, Shape, ShapeGeometry } from 'three';
import { SCALE } from 'utils/3d';
import { buildSpaceWallSegments, WallSegment3D } from 'utils/3d-polygon';
import { isPointInPolygon } from 'utils/footprint';
import {
  EdgeInterval,
  EdgeSegment,
  getHiddenIntervalsPerEdge,
  getVisibleEdgeSegments,
  getVisibleIntervalsPerEdge
} from 'utils/polygon-shared-edges';
import { polygonAreaAbs } from 'utils/space-gap-fill';
import { getSpaceVertices } from 'utils/space-polygon';

export const FOOTPRINT_WALL_OPACITY = 0.3;
export const FOOTPRINT_WALL_THICKNESS = 0.1;
export const FOOTPRINT_FLOOR_Y = 0.01;
export const FOOTPRINT_FLOOR_TILE_SIZE_FT = 10;
const MIN_FOOTPRINT_FLOOR_AREA_SQFT = 1e-3;

export type FootprintFloorRegion = {
  outer: PositionType[];
  holes: PositionType[][];
};

export function getLevelSpacePolygonsFeet(
  level: Pick<LevelType, 'sections'>
): PositionType[][] {
  const polygons: PositionType[][] = [];
  for (const section of level.sections) {
    for (const space of section.spaces) {
      const origin = space.position;
      if (origin) {
        const relative = getSpaceVertices(space);
        if (relative.length >= 3) {
          polygons.push(
            relative.map((vertex) => ({
              x: origin.x + vertex.x,
              y: origin.y + vertex.y
            }))
          );
        }
      }
    }
  }
  return polygons;
}

export function getUncoveredFootprintVisibleIntervals(
  footprint: PositionType[],
  spacePolygons: PositionType[][]
): EdgeInterval[][] {
  if (footprint.length < 3) return [];
  if (spacePolygons.length === 0) {
    return footprint.map(() => [{ start: 0, end: 1 }]);
  }
  const hidden = getHiddenIntervalsPerEdge(footprint, spacePolygons);
  return getVisibleIntervalsPerEdge(hidden);
}

export function getUncoveredFootprintSegments(
  footprint: PositionType[],
  spacePolygons: PositionType[][]
): EdgeSegment[] {
  const visible = getUncoveredFootprintVisibleIntervals(
    footprint,
    spacePolygons
  );
  return getVisibleEdgeSegments(footprint, visible);
}

export function buildFootprintWallSegments(
  footprint: PositionType[],
  spacePolygons: PositionType[][],
  scale: number = SCALE,
  wallThickness: number = FOOTPRINT_WALL_THICKNESS
): WallSegment3D[] {
  const visible = getUncoveredFootprintVisibleIntervals(
    footprint,
    spacePolygons
  );
  if (visible.length === 0) return [];
  const localVertices = footprint.map((point) => ({
    x: point.x * scale,
    z: point.y * scale
  }));
  return buildSpaceWallSegments(localVertices, wallThickness, visible);
}

export function buildFootprintsWallSegments(
  footprints: FootprintPolygonsType,
  spacePolygons: PositionType[][],
  scale: number = SCALE,
  wallThickness: number = FOOTPRINT_WALL_THICKNESS
): WallSegment3D[] {
  return footprints.flatMap((footprint) =>
    buildFootprintWallSegments(footprint, spacePolygons, scale, wallThickness)
  );
}

function polygonToClipping(vertices: PositionType[]): Polygon {
  const ring = vertices.map(
    (vertex) => [vertex.x, vertex.y] as [number, number]
  );
  return [[...ring, ring[0]]];
}

function ringToPoints(ring: [number, number][]): PositionType[] {
  if (ring.length < 2) return [];
  const first = ring[0];
  const last = ring[ring.length - 1];
  const closed = first[0] === last[0] && first[1] === last[1];
  const open = closed ? ring.slice(0, -1) : ring;
  return open.map(([x, y]) => ({ x, y }));
}

function clippingToFloorRegions(mp: MultiPolygon): FootprintFloorRegion[] {
  const regions: FootprintFloorRegion[] = [];
  for (const poly of mp) {
    const outerRing = poly[0];
    if (outerRing && outerRing.length >= 4) {
      const outer = ringToPoints(outerRing);
      if (outer.length >= 3) {
        const holes: PositionType[][] = [];
        for (let i = 1; i < poly.length; i += 1) {
          const hole = ringToPoints(poly[i]);
          if (hole.length >= 3) {
            holes.push(hole);
          }
        }
        regions.push({ outer, holes });
      }
    }
  }
  return regions;
}

function floorRegionArea(region: FootprintFloorRegion): number {
  const holeArea = region.holes.reduce(
    (sum, hole) => sum + polygonAreaAbs(hole),
    0
  );
  return polygonAreaAbs(region.outer) - holeArea;
}

export function isPointInFootprintFloorRegion(
  point: PositionType,
  region: FootprintFloorRegion
): boolean {
  if (!isPointInPolygon(point, region.outer)) return false;
  return !region.holes.some((hole) => isPointInPolygon(point, hole));
}

export function getUncoveredFootprintFloorRegions(
  footprint: PositionType[],
  spacePolygons: PositionType[][]
): FootprintFloorRegion[] {
  if (footprint.length < 3) return [];

  const validSpaces = spacePolygons.filter((polygon) => polygon.length >= 3);
  if (validSpaces.length === 0) {
    return [{ outer: footprint, holes: [] }];
  }

  try {
    const footprintClip = polygonToClipping(footprint);
    const [first, ...rest] = validSpaces.map(polygonToClipping);
    const roomsUnion = polygonClipping.union(first, ...rest);
    const leftover = polygonClipping.difference(footprintClip, ...roomsUnion);
    return clippingToFloorRegions(leftover).filter(
      (region) => floorRegionArea(region) > MIN_FOOTPRINT_FLOOR_AREA_SQFT
    );
  } catch {
    return [];
  }
}

export function getUncoveredFootprintsFloorRegions(
  footprints: FootprintPolygonsType,
  spacePolygons: PositionType[][]
): FootprintFloorRegion[] {
  return footprints.flatMap((footprint) =>
    getUncoveredFootprintFloorRegions(footprint, spacePolygons)
  );
}

function addFootprintRingToPath(
  path: Shape | Path,
  vertices: PositionType[],
  scale: number
): void {
  if (vertices.length === 0) return;
  path.moveTo(vertices[0].x * scale, -vertices[0].y * scale);
  for (let i = 1; i < vertices.length; i += 1) {
    path.lineTo(vertices[i].x * scale, -vertices[i].y * scale);
  }
  path.closePath();
}

export function createFootprintFloorGeometry(
  region: FootprintFloorRegion,
  scale: number = SCALE,
  tileSizeInFeet: number = FOOTPRINT_FLOOR_TILE_SIZE_FT
): ShapeGeometry {
  const shape = new Shape();
  addFootprintRingToPath(shape, region.outer, scale);
  for (const hole of region.holes) {
    const path = new Path();
    addFootprintRingToPath(path, hole, scale);
    shape.holes.push(path);
  }

  const geometry = new ShapeGeometry(shape);
  const positionAttr = geometry.getAttribute('position');
  const uvs = new Float32Array(positionAttr.count * 2);
  for (let i = 0; i < positionAttr.count; i += 1) {
    const sx = positionAttr.getX(i);
    const sy = positionAttr.getY(i);
    uvs[i * 2] = sx / scale / tileSizeInFeet;
    uvs[i * 2 + 1] = -sy / scale / tileSizeInFeet;
  }
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  return geometry;
}
